import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import AdCreditsSuccessModal from "./AdCreditsSuccessModal";
import BillingBackButton from "./BillingBackButton";
import amountWalletIcon from "../../../assets/images/icons/billing/amount-wallet.svg";
import maximizeIcon from "../../../assets/images/icons/billing/maximize.svg";
import minimizeIcon from "../../../assets/images/icons/billing/minimize.svg";
import { upsertBillingProfile, addCredits, getBillingProfile } from "../../../hook/useAdsApi";

const PRESET_AMOUNTS = [15, 25, 50, 100];
const SPEND_CAP = 50; // placeholder - GET /ad-account/spending-controls' cap

// Each Stripe split-element mounts inside the same box the static pass already built (Card
// number/Expiry/CVV), so the layout stays pixel-identical to Figma - only these three fields are
// real Stripe elements; Cardholder Name stays a plain input (sent as billing_details.name, never
// tokenized). This is the split-element alternative to a single combined CardElement (used by
// wallet.js) - chosen specifically to keep the existing 3-box layout instead of collapsing it.
const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "14px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

export default function AdAddCredits() {
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const currency = location.state?.currency || "USD";
  const adAccount = location.state?.adAccount;

  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [amountOpen, setAmountOpen] = useState(true);
  const [addCardOpen, setAddCardOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);

  const refreshBalance = () =>
    getBillingProfile()
      .then((res) => setCurrentBalance(Number(res?.credit_balance || 0)))
      .catch(() => {});

  useEffect(() => {
    refreshBalance();
  }, []);

  const chosenAmount = customAmount ? Number(customAmount) : amount;
  const subtotal = chosenAmount || 0;
  const availableCreditAfter = currentBalance + subtotal;

  const handleContinue = async () => {
    if (!subtotal || subtotal <= 0) {
      toast.error("Enter an amount to continue");
      return;
    }
    if (!stripe || !elements) {
      toast.error("Payment form is still loading, try again in a moment");
      return;
    }
    if (!cardholderName.trim()) {
      toast.error("Enter the cardholder name");
      return;
    }

    setSubmitting(true);
    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { name: cardholderName },
      });
      if (stripeError) {
        toast.error(stripeError.message);
        return;
      }

      // No payment_method_id here - no card is ever saved via profile setup for either billing
      // model (stakeholder decision). This call only configures the model/currency; the fresh
      // card just tokenized above goes straight to addCredits below instead.
      await upsertBillingProfile({
        billing_model: "prepaid",
        currency_code: currency,
      });

      const { transaction } = await addCredits({ amount: subtotal, payment_method_id: paymentMethod.id });
      await refreshBalance();

      setSuccessData({
        // stripe_payment_intent_id (e.g. "pi_3P8x2K...") is a real, Stripe-dashboard-traceable
        // reference - showing the raw AdBillingTransaction.id (a plain sequential row number,
        // e.g. "2") to the user isn't meaningful, so it's only a fallback if that's ever missing.
        transactionId: transaction?.stripe_payment_intent_id || `TXN-${String(transaction?.id || "").padStart(6, "0")}`,
        amount: transaction?.amount ?? subtotal,
        createdAt: transaction?.created_at,
        // Built from the just-tokenized Stripe payment method directly - the backend no longer
        // persists/returns a "default" card to read this from.
        paymentMethod: paymentMethod.card
          ? `${paymentMethod.card.brand || "Card"} •••• ${paymentMethod.card.last4 || ""}`
          : "Card",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong, try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-4xl">
            <div className="mb-6 flex flex-wrap gap-3 items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Prepaid Billing Setup</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Configure your prepaid billing preferences and payment method.
                </p>
                <div className="border-b border-gray-200 mt-2 w-full max-w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="sm:col-span-2">

              <div
                className="rounded-2xl p-5 mb-5 text-white flex items-center gap-4"
                style={{ backgroundImage: "linear-gradient(90deg, #1de78b 2.6%, #08aeea 97.5%)" }}
              >
                <img src={amountWalletIcon} alt="" className="w-8 h-8 brightness-0 invert" />
                <div>
                  <p className="text-sm opacity-90">Your Available Balance</p>
                  <p className="text-2xl font-bold">{currency} {currentBalance.toLocaleString()}</p>
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-5 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Choose your Amount</h3>
                  <button type="button" onClick={() => setAmountOpen((o) => !o)}>
                    <img src={amountOpen ? minimizeIcon : maximizeIcon} alt="" className="w-6 h-6" />
                  </button>
                </div>
                {amountOpen && (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {PRESET_AMOUNTS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => {
                            setAmount(preset);
                            setCustomAmount("");
                          }}
                          className={`flex items-center gap-2 rounded-lg border bg-white py-2.5 px-3 text-sm font-semibold ${
                            !customAmount && amount === preset
                              ? "border-[#095228] bg-[#ecfbf3] text-[#095228]"
                              : "border-gray-200 text-gray-700"
                          }`}
                        >
                          <img src={amountWalletIcon} alt="" className="w-4 h-4" /> {currency} {preset.toFixed(2)}
                        </button>
                      ))}
                    </div>
                    <label className="block text-sm text-gray-600 mb-1">Other nominal</label>
                    <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white max-w-sm">
                      <span className="px-3 py-2 text-sm border-r border-gray-300 bg-gray-50 flex items-center gap-1">
                        {currency} <span className="text-xs">▾</span>
                      </span>
                      <input
                        type="number"
                        min="1"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="0"
                        className="flex-1 min-w-0 px-3 py-2 text-sm outline-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="rounded-2xl bg-gray-50 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Add Card</h3>
                  <button type="button" onClick={() => setAddCardOpen((o) => !o)}>
                    <img src={addCardOpen ? minimizeIcon : maximizeIcon} alt="" className="w-6 h-6" />
                  </button>
                </div>
                {addCardOpen && (
                  <>
                    <label className="block text-sm text-gray-600 mb-1">Card number</label>
                    <div className="border border-gray-300 rounded-lg px-3 py-2.5 mb-3 bg-white">
                      <CardNumberElement options={ELEMENT_OPTIONS} />
                    </div>
                    <label className="block text-sm text-gray-600 mb-1">Cardholder name</label>
                    <input
                      type="text"
                      placeholder="Name"
                      value={cardholderName}
                      onChange={(e) => setCardholderName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                    />
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
                        <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                          <CardExpiryElement options={ELEMENT_OPTIONS} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">CVV / CVC</label>
                        <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                          <CardCvcElement options={ELEMENT_OPTIONS} />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="rounded-2xl border border-gray-200 p-5">
                <h3 className="font-bold text-sm mb-1">Pre-paid Summary</h3>
                <p className="text-xs text-gray-500 mb-4">Review your details before proceeding.</p>
                <div className="flex justify-between text-sm mb-4">
                  <span className="text-gray-500">Total</span>
                  <span className="font-semibold">{currency} {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-4">
                  <span className="text-gray-500">Available Credit (after this)</span>
                  <span className="font-semibold text-[#095228]">{currency} {availableCreditAfter.toFixed(2)}</span>
                </div>

                <div className="rounded-xl bg-blue-50 p-3 flex gap-2 items-start mb-4">
                  <span className="text-blue-500 text-sm">ⓘ</span>
                  <p className="text-xs text-blue-700">
                    Your total spend will be capped at the platform approved limit of {currency} {SPEND_CAP}. You
                    cannot exceed this limit.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={submitting}
                  className="w-full bg-[#095228] text-white font-semibold py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-60"
                >
                  {submitting ? "Processing..." : "Continue"}
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  By proceeding, you agree to our{" "}
                  {/* No Billing Policy page exists anywhere in the app yet - left as plain text
                      rather than a fake link until a real page exists to point it at. */}
                  <span className="text-gray-500">Billing Policy</span> and{" "}
                  <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-[#095228] underline">
                    Terms of Service
                  </a>.
                </p>
              </div>
            </div>
            </div>
          </div>
        </main>
      </main>

      {successData && (
        <AdCreditsSuccessModal
          mode="prepaid"
          transactionId={successData.transactionId}
          amount={successData.amount}
          currency={currency}
          createdAt={successData.createdAt}
          paymentMethod={successData.paymentMethod}
          onDone={() => {
            setSuccessData(null);
            navigate(adAccount ? "/ads/create" : "/ads/billing", { state: { adAccount } });
          }}
        />
      )}
    </div>
  );
}
